import { useState } from 'react';
// @mui
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
//
import axios, { Endpoint, formatEndpoint } from 'src/utils/axios';
import Iconify from 'src/components/iconify';
import { IftaFiling, IftaUploadedFile } from 'src/models';

// ----------------------------------------------------------------------

type Props = {
  filingId: number;
  files: IftaUploadedFile[];
  lineCountFor: (fileName: string) => number;
  removeEndpoint: Endpoint;
  onRemoved: (filing: IftaFiling) => void;
};

export default function UploadedFilesList({ filingId, files, lineCountFor, removeEndpoint, onRemoved }: Props) {
  const [removing, setRemoving] = useState('');

  if (files.length === 0) {
    return null;
  }

  const removeFile = async (fileName: string) => {
    setRemoving(fileName);
    try {
      const response = await axios.post(formatEndpoint(removeEndpoint, { id: filingId }), { fileName });
      onRemoved(response.data.filing);
    } finally {
      setRemoving('');
    }
  };

  return (
    <Stack spacing={1}>
      {files.map((file) => (
        <Card key={file.fileName} variant="outlined" sx={{ px: 2, py: 1.25 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Iconify icon="solar:file-text-bold" width={20} sx={{ color: 'primary.dark' }} />
            <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap>
                {file.fileName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {lineCountFor(file.fileName)} lines
              </Typography>
            </Stack>
            <IconButton
              size="small"
              onClick={() => removeFile(file.fileName)}
              disabled={removing === file.fileName}
              aria-label={`Remove ${file.fileName}`}
              data-testid={`remove-file-${file.fileName}`}
            >
              <Iconify icon="solar:trash-bin-trash-bold" width={18} sx={{ color: 'error.main' }} />
            </IconButton>
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}
